import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Heart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createDonationCheckout } from "@/lib/api";
import { IMPACT_ANCHORS } from "@/lib/content";
import PayPalDonate from "@/components/PayPalDonate";

const PRESETS = [25, 50, 150, 500];

export default function DonateDialog({ trigger, defaultDesignation = "Where Needed Most" }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [frequency, setFrequency] = useState("one-time");
  const [designation, setDesignation] = useState(defaultDesignation);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const finalAmount = custom ? parseFloat(custom) : amount;
  const anchor = IMPACT_ANCHORS.find((a) => a.amount === finalAmount);
  const formValid = !!(form.name && form.email && finalAmount && finalAmount > 0);

  const buildDonation = () => {
    if (!formValid) {
      toast.error("Please add your name, email, and a gift amount first.");
      return null;
    }
    return { ...form, amount: finalAmount, frequency: "one-time", designation, origin_url: window.location.origin };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !finalAmount || finalAmount <= 0) {
      toast.error("Please add your name, email, and a gift amount.");
      return;
    }
    setLoading(true);
    try {
      const res = await createDonationCheckout({
        ...form,
        amount: finalAmount,
        frequency,
        designation,
        origin_url: window.location.origin,
      });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.error("Could not start secure checkout. Please try again.");
        setLoading(false);
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg bg-cream border-plum/10 max-h-[90vh] overflow-y-auto" data-testid="donate-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl text-plum flex items-center gap-2">
            <Heart className="text-gold-dark" size={24} /> Make a Gift
          </DialogTitle>
          <DialogDescription className="text-stone">
            Your generosity fuels mentorship, outreach, and a home being restored for youth.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div className="flex gap-2 p-1 bg-plum/5 rounded-full w-fit">
            {["monthly", "one-time"].map((f) => (
              <button
                type="button"
                key={f}
                onClick={() => setFrequency(f)}
                data-testid={`freq-${f}`}
                className={`px-5 py-2 rounded-full text-sm font-medium capitalize transition-colors ${
                  frequency === f ? "bg-plum text-cream" : "text-plum hover:bg-plum/10"
                }`}
              >
                {f === "one-time" ? "One-Time" : "Monthly"}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-4 gap-2">
            {PRESETS.map((p) => (
              <button
                type="button"
                key={p}
                data-testid={`amount-${p}`}
                onClick={() => { setAmount(p); setCustom(""); }}
                className={`py-3 rounded-xl border text-base font-semibold transition-all ${
                  !custom && amount === p
                    ? "border-gold bg-gold text-plum shadow"
                    : "border-plum/15 bg-white text-plum hover:border-gold"
                }`}
              >
                ${p}
              </button>
            ))}
          </div>

          <div>
            <Label className="text-plum text-sm">Or enter a custom amount</Label>
            <div className="relative mt-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone">$</span>
              <Input
                type="number"
                min="1"
                placeholder="Custom amount"
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                data-testid="amount-custom"
                className="pl-8 h-12 bg-white border-plum/15"
              />
            </div>
          </div>

          {anchor && (
            <div className="text-sm bg-plum/5 text-plum rounded-xl px-4 py-3" data-testid="impact-anchor">
              <span className="font-semibold">${anchor.amount}</span> {anchor.label.toLowerCase()}.
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-plum text-sm">Designate my gift to</Label>
            <Select value={designation} onValueChange={setDesignation}>
              <SelectTrigger className="h-12 bg-white border-plum/15" data-testid="designation-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["Where Needed Most", "Foster Care Home", "Naomi's Guidance", "Community Outreach", "Let's Adopt a Cause", "Daycare Scholarships"].map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="donor-name" className="h-12 bg-white border-plum/15" />
            <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="donor-email" className="h-12 bg-white border-plum/15" />
          </div>
          <Input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="donor-phone" className="h-12 bg-white border-plum/15" />

          <button
            type="submit"
            disabled={loading}
            data-testid="donate-submit-btn"
            className="w-full h-13 py-4 rounded-full bg-gold hover:bg-gold-dark text-plum font-semibold text-base transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Heart size={18} />}
            {loading ? "Redirecting to secure checkout..." : `Give $${finalAmount || 0}${frequency === "monthly" ? " / month" : ""}`}
          </button>
          <p className="text-xs text-stone text-center">
            Secure checkout powered by Stripe — card, Apple Pay &amp; Google Pay accepted. You'll be redirected to complete your gift.
          </p>

          {frequency === "one-time" && (
            <PayPalDonate getDonation={buildDonation} disabled={!formValid} forceKey={`${finalAmount}-${designation}`} />
          )}

          <p className="text-sm text-plum text-center bg-plum/5 rounded-xl py-2.5" data-testid="donate-cashapp">
            Prefer Cash App? Send your gift to <span className="font-heading text-lg text-plum font-semibold">$GreaterfaithD</span>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
